<script setup lang="ts">
  import {
    computed,
    nextTick,
    onActivated,
    onBeforeUnmount,
    onMounted,
    reactive,
    ref,
    watch,
  } from 'vue';
  import {
    actions as actionTypes,
    useUserComponent,
    useHelpers,
    createComponentScope,
    Resource,
    uniqueID,
  } from '@integro/core';
  import type { IAppHelpers } from '@integro/core';
  import * as _ from 'lodash';

  defineOptions({
    name: 'AddFixedAssetToRentalModal',
  });

  const componentMetadata = {
    kind: 'page',
  };

  const emit = defineEmits(['reload', 'close', 'save']);

  const props = defineProps({
    asset: {
      type: Object,
      required: true,
    },
    modal: {
      type: Boolean,
      required: false,
      default: false,
    },
    modalName: {
      type: String,
      required: false,
    },
  });

  const {
    $app,
    $fn,
    $i18n,
    $store,
    $config,
    $imodal,
    $notification,
    $tabs,
    $route,
    $router,
    gettext,
    pgettext,
    ngettext,
    npgettext,
    schemaDefaults,
    getSchema,
    searchDataSource,
    randomElementId,
    datetime,
    date,
    time,
    number,
    money,
    distance,
    username,
    filesize,
  } = useHelpers();

  const mainForm = ref(null);

  const $refs = {
    get mainForm() {
      return mainForm.value;
    },
  };

  const formId = ref(null);
  const item = ref(null);
  const ux = ref([]);
  const b_7315092382398562_noShow = computed(() => false);
  const b_7315092382398562_modalBindings = computed(() => ({
    size: 'md',
  }));
  const b_5534025912102772_auto = computed(() => false);
  const b_5731695935601903_dataSource = computed(() => ({
    name: 'suggest_rental',
  }));

  const $data = computed(() => ({
    formId: formId.value,
    item: item.value,
    ux: ux.value,
    b_7315092382398562_noShow: b_7315092382398562_noShow.value,
    b_7315092382398562_modalBindings: b_7315092382398562_modalBindings.value,
    b_5534025912102772_auto: b_5534025912102772_auto.value,
    b_5731695935601903_dataSource: b_5731695935601903_dataSource.value,
  }));

  const $$load_formId = async () => {
    return $fn.value.randomElementId();
  };
  const $$load_item = async () => {
    return $fn.value.schemaDefaults('CreateRentalAsset', {
      asset: props.asset,
      valid_from: $fn.value.today(),
    });
  };

  const {
    loading,
    error,
    loaded,
    reloading,
    showLoadingIndicator,
    loadDataThrottled,
    friendlyName,
    loadData,
    touchLastLoaded,
    handleError,
    setData,
    forms,
    registerForm,
    unregisterForm,
    canLoadData,
    isModalComponent,
    modalName,
    pageDocument: _pageDocument,
    routeTab,
    ux: _ux,
    componentMetadata: _componentMetadata,
    $uid,
    lastLoaded,
  } = useUserComponent(props, {
    metadata: componentMetadata,
    dataSpecs: [
      { name: 'formId' },
      { name: 'item' },
      { name: 'ux' },
      { name: 'b_7315092382398562_noShow', nodeId: '7315092382398562' },
      { name: 'b_7315092382398562_modalBindings', nodeId: '7315092382398562' },
      { name: 'b_5534025912102772_auto', nodeId: '5534025912102772' },
      { name: 'b_5731695935601903_dataSource', nodeId: '5731695935601903' },
    ],
    loaders: { formId: $$load_formId, item: $$load_item },
    ux: {},
    dataRefs: {
      formId,
      item,
      ux,
      b_7315092382398562_noShow,
      b_7315092382398562_modalBindings,
      b_5534025912102772_auto,
      b_5731695935601903_dataSource,
    },
  });

  const $component = createComponentScope(
    {
      $app,
      $fn,
      $i18n,
      $store,
      $imodal,
      $notification,
      $tabs,
      $router,
    } as IAppHelpers,
    {
      emit,
      forms,
      error,
      isModalComponent,
      modalName,
      componentMetadata,
      loadData,
      registerForm,
      unregisterForm,
      setData,
      touchLastLoaded,
      handleError,
    }
  );

  const pageDocument = computed(() => {
    return {
      title: $fn.value.pgettext('AddFixedAssetToRentalModal', 'Add to Rental'),
    };
  });
  const documentTitle = computed(() => {
    return pageDocument.value
      ? Array.isArray(pageDocument.value.title)
        ? pageDocument.value.title.filter(Boolean).join(' ')
        : pageDocument.value.title
      : null;
  });

  watch(
    () => props.asset,
    (newValue, oldValue) => {
      if (_.isEqual(newValue, oldValue)) return;
      loadDataThrottled('asset');
    }
  );
  watch(
    () => props.modal,
    (newValue, oldValue) => {
      if (_.isEqual(newValue, oldValue)) return;
      loadDataThrottled('modal');
    }
  );
  watch(
    () => props.modalName,
    (newValue, oldValue) => {
      if (_.isEqual(newValue, oldValue)) return;
      loadDataThrottled('modalName');
    }
  );

  watch(
    pageDocument,
    (newDoc) => {
      if (newDoc) {
        _pageDocument.value = newDoc;
      }
    },
    { immediate: true }
  );

  const act_7315092382398562_closeModal_dytmljbxw_getActionArgs = async (
    $event
  ) => {
    return {
      name: props.modalName,
      result: {
        close: true,
        cancel: true,
      },
    };
  };
  const act_1040167445267876_closeModal_c2x9bd2xo_getActionArgs = async (
    $event
  ) => {
    return {
      result: {
        close: true,
        ok: true,
        value: $event.data.response.data,
      },
    };
  };
  const act_1040167445267876_request_q5m1acb79_getActionArgs = async (
    $event
  ) => {
    return {
      operation: 'create_rental_asset',
      data: item.value,
    };
  };

  const act_7315092382398562_closeModal_dytmljbxw =
    new actionTypes.CloseModalAction({
      actionArgs: act_7315092382398562_closeModal_dytmljbxw_getActionArgs,
    });
  const inst_evh_7315092382398562_close_s7iejptyq =
    new actionTypes.EventHandlerImpl({
      action: act_7315092382398562_closeModal_dytmljbxw,
      event: 'close',
      displayName: 'closeModal',
    });
  const inst_evh_7315092382398562_close = new actionTypes.EventHandlerGroup({
    handlers: [inst_evh_7315092382398562_close_s7iejptyq],
    event: 'close',
  });
  const act_7315092382398562_reloadComponentData_e9k5r8m0l =
    new actionTypes.ReloadComponentDataAction({});
  const inst_evh_7315092382398562_reload_d4b9saqgx =
    new actionTypes.EventHandlerImpl({
      action: act_7315092382398562_reloadComponentData_e9k5r8m0l,
      event: 'reload',
      displayName: 'reloadComponentData',
    });
  const inst_evh_7315092382398562_reload = new actionTypes.EventHandlerGroup({
    handlers: [inst_evh_7315092382398562_reload_d4b9saqgx],
    event: 'reload',
  });
  const act_8564662037462133_closeModal_cgy6kllru =
    new actionTypes.CloseModalAction({});
  const inst_evh_8564662037462133_close_lva8iuumy =
    new actionTypes.EventHandlerImpl({
      action: act_8564662037462133_closeModal_cgy6kllru,
      event: 'close',
      displayName: 'closeModal',
    });
  const inst_evh_8564662037462133_close = new actionTypes.EventHandlerGroup({
    handlers: [inst_evh_8564662037462133_close_lva8iuumy],
    event: 'close',
  });
  const act_1040167445267876_closeModal_c2x9bd2xo =
    new actionTypes.CloseModalAction({
      actionArgs: act_1040167445267876_closeModal_c2x9bd2xo_getActionArgs,
    });
  const inst_evh_1040167445267876_success_1pu5ye4bz =
    new actionTypes.EventHandlerImpl({
      action: act_1040167445267876_closeModal_c2x9bd2xo,
      event: 'success',
      displayName: 'closeModal',
    });
  const act_1040167445267876_request_q5m1acb79 = new actionTypes.RequestAction({
    actionArgs: act_1040167445267876_request_q5m1acb79_getActionArgs,
    displayName: 'submit',
    events: [inst_evh_1040167445267876_success_1pu5ye4bz],
  });
  const inst_evh_1040167445267876_submit_ef1c0qqgh =
    new actionTypes.EventHandlerImpl({
      action: act_1040167445267876_request_q5m1acb79,
      event: 'submit',
      displayName: 'submit',
    });
  const inst_evh_1040167445267876_submit = new actionTypes.EventHandlerGroup({
    handlers: [inst_evh_1040167445267876_submit_ef1c0qqgh],
    event: 'submit',
  });
  const act_2434514588044777_closeComponent_ewigs79zs =
    new actionTypes.CloseComponentAction({});
  const inst_evh_2434514588044777_clickPreventStop_i28a89xnj =
    new actionTypes.EventHandlerImpl({
      action: act_2434514588044777_closeComponent_ewigs79zs,
      event: 'click.prevent.stop',
      displayName: 'closeComponent',
    });
  const inst_evh_2434514588044777_clickPreventStop =
    new actionTypes.EventHandlerGroup({
      handlers: [inst_evh_2434514588044777_clickPreventStop_i28a89xnj],
      event: 'click.prevent.stop',
    });

  const actions = {
    inst_evh_7315092382398562_close_s7iejptyq,
    inst_evh_7315092382398562_close,
    inst_evh_7315092382398562_reload_d4b9saqgx,
    inst_evh_7315092382398562_reload,
    inst_evh_8564662037462133_close_lva8iuumy,
    inst_evh_8564662037462133_close,
    inst_evh_1040167445267876_success_1pu5ye4bz,
    inst_evh_1040167445267876_submit_ef1c0qqgh,
    submit: inst_evh_1040167445267876_submit_ef1c0qqgh,
    inst_evh_1040167445267876_submit,
    inst_evh_2434514588044777_clickPreventStop_i28a89xnj,
    inst_evh_2434514588044777_clickPreventStop,
  };

  const evh_7315092382398562_close_s7iejptyq = async (event, scope) => {
    return await actions.inst_evh_7315092382398562_close_s7iejptyq.executeFromDOM(
      $component,
      event,
      scope
    );
  };
  const evh_7315092382398562_close = async (event, scope) => {
    return await actions.inst_evh_7315092382398562_close.executeFromDOM(
      $component,
      event,
      scope
    );
  };
  const evh_7315092382398562_reload_d4b9saqgx = async (event, scope) => {
    return await actions.inst_evh_7315092382398562_reload_d4b9saqgx.executeFromDOM(
      $component,
      event,
      scope
    );
  };
  const evh_7315092382398562_reload = async (event, scope) => {
    return await actions.inst_evh_7315092382398562_reload.executeFromDOM(
      $component,
      event,
      scope
    );
  };
  const evh_8564662037462133_close_lva8iuumy = async (event, scope) => {
    return await actions.inst_evh_8564662037462133_close_lva8iuumy.executeFromDOM(
      $component,
      event,
      scope
    );
  };
  const evh_8564662037462133_close = async (event, scope) => {
    return await actions.inst_evh_8564662037462133_close.executeFromDOM(
      $component,
      event,
      scope
    );
  };
  const evh_1040167445267876_success_1pu5ye4bz = async (event, scope) => {
    return await actions.inst_evh_1040167445267876_success_1pu5ye4bz.executeFromDOM(
      $component,
      event,
      scope
    );
  };
  const evh_1040167445267876_submit_ef1c0qqgh = async (event, scope) => {
    return await actions.inst_evh_1040167445267876_submit_ef1c0qqgh.executeFromDOM(
      $component,
      event,
      scope
    );
  };
  const evh_1040167445267876_submit = async (event, scope) => {
    return await actions.inst_evh_1040167445267876_submit.executeFromDOM(
      $component,
      event,
      scope
    );
  };
  const evh_2434514588044777_clickPreventStop_i28a89xnj = async (
    event,
    scope
  ) => {
    return await actions.inst_evh_2434514588044777_clickPreventStop_i28a89xnj.executeFromDOM(
      $component,
      event,
      scope
    );
  };
  const evh_2434514588044777_clickPreventStop = async (event, scope) => {
    return await actions.inst_evh_2434514588044777_clickPreventStop.executeFromDOM(
      $component,
      event,
      scope
    );
  };

  defineExpose({
    documentTitle,
    pageDocument: _pageDocument,
    routeTab,
    loadData,
    loading,
    loaded,
    error,
  });
</script>

<template>
  <ItpPage
    :header="documentTitle"
    :loaded="loaded"
    :noShow="b_7315092382398562_noShow"
    :loading="showLoadingIndicator"
    :reloading="reloading"
    :error="error"
    :modal="modal"
    :modalBindings="b_7315092382398562_modalBindings"
    itp-cname="AddFixedAssetToRentalModal"
    @close="evh_7315092382398562_close($event, {})"
    @reload="evh_7315092382398562_reload($event, {})"
  >
    <template #header>
      <UcHeader
        :card="!modal"
        :modal="modal"
        :title="documentTitle"
        v-if="loaded"
        @close="evh_8564662037462133_close($event, {})"
      >
      </UcHeader>
    </template>
    <UcBody :card="!modal" :modal="modal" v-if="loaded">
      <ItpForm
        name="addFixedAssetToRental"
        :id="formId"
        class="row"
        ref="mainForm"
        @submit="evh_1040167445267876_submit($event, {})"
      >
        <ItpCol :auto="b_5534025912102772_auto">
          <ItpFormGroup
            :label-cols-md="3"
            :content-cols-md="9"
            name="rental_id"
            :label="pgettext('AddFixedAssetToRentalModal', 'Rental')"
          >
            <ItpSelect2
              name="parent_id"
              v-model="item.rental_id"
              :value="item.rental_id"
              @input="
                !($event && typeof $event.stopPropagation === 'function') &&
                (item.rental_id =
                  $event && typeof $event === 'object' && 'data' in $event
                    ? $event.data
                    : $event)
              "
              :dataSource="b_5731695935601903_dataSource"
              valueField="id"
              textField="name"
              use-option-template-for-selected-option
              optionTemplate='<UcSuggestRentalOption :option="option"></UcSuggestRentalOption>'
            >
            </ItpSelect2>
          </ItpFormGroup>
          <ItpFormGroup
            :label-cols-md="3"
            :content-cols-md="9"
            name="valid_from"
            :label="pgettext('AddFixedAssetToRentalModal', 'Valid From')"
          >
            <DatePicker
              inputClass="form-control form-control-md"
              name="valid_from"
              v-model="item.valid_from"
              :value="item.valid_from"
              @input="
                !($event && typeof $event.stopPropagation === 'function') &&
                (item.valid_from =
                  $event && typeof $event === 'object' && 'data' in $event
                    ? $event.data
                    : $event)
              "
              format="YYYY-MM-DD"
              valueType="format"
            >
            </DatePicker>
          </ItpFormGroup>
          <ItpFormGroup
            :label-cols-md="3"
            :content-cols-md="9"
            name="valid_to"
            :label="pgettext('AddFixedAssetToRentalModal', 'Valid To')"
          >
            <DatePicker
              inputClass="form-control form-control-md"
              name="valid_from"
              v-model="item.valid_to"
              :value="item.valid_to"
              @input="
                !($event && typeof $event.stopPropagation === 'function') &&
                (item.valid_to =
                  $event && typeof $event === 'object' && 'data' in $event
                    ? $event.data
                    : $event)
              "
              format="YYYY-MM-DD"
              valueType="format"
            >
            </DatePicker>
          </ItpFormGroup>
          <ItpFormGroup
            :label-cols-md="3"
            :content-cols-md="9"
            name="valid_to"
            :label="pgettext('AddFixedAssetToRentalModal', 'Notes')"
          >
            <ItpFormTextarea
              name="notes"
              v-model="item.notes"
              :value="item.notes"
              @input="
                !($event && typeof $event.stopPropagation === 'function') &&
                (item.notes =
                  $event && typeof $event === 'object' && 'data' in $event
                    ? $event.data
                    : $event)
              "
            >
            </ItpFormTextarea>
          </ItpFormGroup>
        </ItpCol>
      </ItpForm>
    </UcBody>
    <template #footer>
      <UcFooter :card="!modal" :modal="modal" v-if="loaded">
        <ItpButton
          type="submit"
          :text="pgettext('AddFixedAssetToRentalModal', 'Add')"
          :form="forms.addFixedAssetToRental"
          :spinning="actions.submit.isRunning"
        >
        </ItpButton>
        <ItpButton
          :text="gettext('Cancel')"
          variant="default"
          @click.prevent.stop="
            evh_2434514588044777_clickPreventStop($event, {})
          "
        >
        </ItpButton>
      </UcFooter>
    </template>
  </ItpPage>
</template>
